// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { safeJsonParse, StyledBox } from "@pace/XDL/Utilities";
import React, { FC } from "react";

const enum fields {
    title = "title",
    description = "description",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
    };
}

export interface HaveQuestionsStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    phone?: LinkPresentationProps;
}

export const haveQuestionsStyles: HaveQuestionsStyles = {};

const styles = haveQuestionsStyles;

type Props = OwnProps & WidgetProps;

const HaveQuestions: FC<Props> = ({ fields }) => {
    const { phone } = useAppSelector(state => {
        const session = getSession(state);
        return {
            phone: safeJsonParse(session.properties?.primarySalesPerson || "").Phone1,
        };
    });
    return (
        <StyledBox {...styles.wrapper}>
            <Typography variant="h5" {...styles.title}>
                {fields.title}
            </Typography>
            <Typography variant="p" {...styles.description}>
                {fields.description}
            </Typography>
            {phone && (
                <Typography variant="p" {...styles.title}>
                    <Link href={`tel:${phone}`} {...styles.phone}>
                        {phone}
                    </Link>
                </Typography>
            )}
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: HaveQuestions,
    definition: {
        group: "Common",
        displayName: "Have Questions",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "Have Questions On Your Purchase?",
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.description,
                editorTemplate: "TextField",
                displayName: "Description",
                defaultValue: "",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
